import React, { useMemo } from "react";
import { Button, Modal, Pagination, PlusIcon } from "@pd/pd-ui-kit";
import { Flex } from "antd";
import { useTranslation } from "react-i18next";

import Wrapper from "~/shared/ui/Wrapper";
import {
  CombinationRuleConditionType,
  CombinationRuleOrderBy,
  ICombinationRule,
  useGetCombinationRules,
} from "~/entity/CombinationRules";
import { useGetPositions } from "~/entity/Positions";
import {
  useGetLocationData,
  useGoToPreviousPage,
  useSetLocationData,
} from "~/hooks/locationData";
import { OpenModal } from "~/pages/Editor/type";
import { usePermissions } from "~/shared/context/PermissionContext";
import { usePaginationParams } from "~/shared/hooks/usePaginationParams";
import { TableV2, useTableSort } from "~/shared/ui/TableV2";

import CombinationRulesFilter from "./Filter";
import ActionButtons from "./model/ActionButtons";
import RuleManagement from "./RuleManagement";
import {
  getCombinationRulesColumns,
  getCombinationRulesSortableApiKeys,
} from "./model/tableAdapter";

import s from "./EditorCombinationRules.less";

const formId = "CombinationRuleManagementForm";

/** Справочник грузим целиком, чтобы отрисовать имена должностей по id. */
const POSITIONS_LOOKUP_COUNT = 1000;

const EditorCombinationRules = () => {
  const { t } = useTranslation();
  const goBack = useGoToPreviousPage();
  const setData = useSetLocationData();
  const setDataReplace = useSetLocationData(true);
  const { hasPermission } = usePermissions();

  const {
    count,
    page,
    openModal,
    currentId,
    sortField,
    sortType: sortTypeRaw,
  } = useGetLocationData();
  // Ключи фильтров приходят из URL и не описаны в типе useGetLocationData
  const {
    mainPositionId,
    createdBy,
    updatedBy,
    conditionTypes: conditionTypesRaw,
    createdAtFrom,
    createdAtTo,
    updatedAtFrom,
    updatedAtTo,
  } = useGetLocationData() as Record<string, unknown>;

  const conditionTypes =
    typeof conditionTypesRaw === "string" && conditionTypesRaw
      ? (conditionTypesRaw
          .split(",")
          .filter(Boolean) as CombinationRuleConditionType[])
      : undefined;

  const sortableApiKeys = getCombinationRulesSortableApiKeys();

  const { sortType: normalizedSortType, onSortChange } = useTableSort({
    sortField,
    sortType: sortTypeRaw,
    sortableColumns: sortableApiKeys,
    setData,
  });

  const tableSortField =
    sortField && sortableApiKeys.includes(sortField) ? sortField : undefined;

  const orderBy = tableSortField
    ? (tableSortField as CombinationRuleOrderBy)
    : undefined;
  const orderDirection =
    orderBy && (normalizedSortType === "ASC" || normalizedSortType === "DESC")
      ? (normalizedSortType.toLowerCase() as "asc" | "desc")
      : undefined;

  // Бэкенд нумерует страницы с 1, в URL страница 0-based
  const { rules, total, isLoading, refetch } = useGetCombinationRules({
    page: page + 1,
    count,
    mainPositionId: mainPositionId ? Number(mainPositionId) : undefined,
    createdBy: createdBy ? String(createdBy) : undefined,
    updatedBy: updatedBy ? String(updatedBy) : undefined,
    conditionTypes,
    createdAtFrom: createdAtFrom ? String(createdAtFrom) : undefined,
    createdAtTo: createdAtTo ? String(createdAtTo) : undefined,
    updatedAtFrom: updatedAtFrom ? String(updatedAtFrom) : undefined,
    updatedAtTo: updatedAtTo ? String(updatedAtTo) : undefined,
    orderBy,
    orderDirection,
  });

  const { positions } = useGetPositions({ count: POSITIONS_LOOKUP_COUNT });

  const positionsMap = useMemo(
    () =>
      new Map<number, string>(
        (positions ?? []).map(({ instanceId, name }) => [instanceId, name]),
      ),
    [positions],
  );

  const columns = getCombinationRulesColumns(t, {
    positionsMap,
    sortField: tableSortField,
    sortType: normalizedSortType,
  });

  const { handleCount, handlePage } = usePaginationParams();

  const canCreate = hasPermission("combinationRules.rules.create");
  const canEdit = hasPermission("combinationRules.rules.edit");
  const isAddModal = openModal === OpenModal.Add && canCreate;
  const isEditModal =
    openModal === OpenModal.Edit && canEdit && Boolean(currentId);
  const isViewModal = openModal === OpenModal.View && Boolean(currentId);
  const isRuleModalOpen = isAddModal || isEditModal || isViewModal;

  const openAddModal = () => setDataReplace("openModal", OpenModal.Add);
  const closeModal = () => setDataReplace("openModal", "", "currentId", "");

  const handleRowEdit = (record: ICombinationRule) => {
    setDataReplace("openModal", OpenModal.Edit, "currentId", record.id);
  };

  const handleRowView = (record: ICombinationRule) => {
    setDataReplace("openModal", OpenModal.View, "currentId", record.id);
  };

  return (
    <Wrapper.Content
      title={t("editor.combination_rules")}
      goBackHandler={goBack}
      txtBtn={t("ui.back")}
      filterComponent={<CombinationRulesFilter />}
      controlComponent={
        <Flex gap={10}>
          {canCreate && (
            <Button
              onClick={openAddModal}
              size={"middle"}
              icon={<PlusIcon size={16} />}
            >
              {t("ui.add")}
            </Button>
          )}
        </Flex>
      }
    >
      <ActionButtons rules={rules} positionsMap={positionsMap} />
      <TableV2
        columns={columns}
        dataSource={rules ?? []}
        loading={isLoading}
        sortableColumns={sortableApiKeys}
        sortField={tableSortField}
        sortType={tableSortField ? normalizedSortType : undefined}
        onSortChange={onSortChange}
        onViewDetails={handleRowView}
        {...(canEdit ? { onRowEdit: handleRowEdit } : {})}
        wrapperClassName={s.editor_table}
      />
      <Pagination
        count={count}
        page={page + 1}
        totalCount={total ?? 0}
        onChangeCount={handleCount}
        onChangePage={handlePage}
      />

      <Modal
        open={isRuleModalOpen}
        title={t("combinationRules.configure_rule")}
        onCancel={closeModal}
        closable
        centered
        destroyOnHidden
        cancelButton={{ children: t("ui.close") }}
        okButton={
          isAddModal || isEditModal
            ? {
                htmlType: "submit",
                form: formId,
                children: t("ui.save"),
              }
            : undefined
        }
      >
        {isRuleModalOpen && (
          <RuleManagement
            refetch={refetch}
            closeModal={closeModal}
            formId={formId}
            positionsMap={positionsMap}
          />
        )}
      </Modal>
    </Wrapper.Content>
  );
};

export default EditorCombinationRules;
